import { CoreButtonView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TWidgetCommonOption } from '@libs/types';
import { Glass, type GlassOptics } from '@samasante/liquid-glass';
import clsx from 'clsx';
import React, { MouseEventHandler } from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TButtonOption } from '../../types/options';
import { VisualView } from '../Image/VisualView';
import pillStyles from './styles/buttonPill.module.css';
import styles from './styles/buttonPillGlass.module.css';
import { getMaxWidth, getPaddingPostfix, PADDING_MAP, PILL_FONT_STYLE_MAP, PILL_HEIGHT_MAP } from './styles/buttonPillUtils';

/**
 * The optical look, fixed at module scope on purpose.
 *
 * <Glass> regenerates its displacement map whenever a shape-affecting optic
 * (depth / curvature / bend / sheen / glow / splay) changes, so animating optics
 * per interaction would rasterize a new map on every hover. Hover and press are
 * expressed through the CSS tint instead, which keeps the map generated exactly
 * once per button size.
 *
 * Differences from the circle variant (ButtonCircleGlassView) all follow from the
 * aspect ratio: a pill is up to ~320px wide but only 28-56px tall, so `mapSize` is
 * 256 rather than 128 (a square map stretched over the long edge under-resolves it
 * at 128), and `depth` / `curvature` are lower — a wide body dome magnifies the
 * backdrop unevenly across the label, so the glass is left to read through the rim.
 */
const GLASS_OPTICS: Partial<GlassOptics> = {
    mapSize: 256,
    frost: 6,
    saturate: 1.2,
    strength: 0.06,
    depth: 0.35,
    curvature: 0.2,
    // The rim meniscus carries the look here, so it stays close to the circle's.
    bend: 0.45,
    bendWidth: 0.14,
    dispersion: 0.3,
    sheen: 0.35,
    sheenWidth: 2,
    glow: 0.12,
    brightness: 0.06,
};
// const GLASS_OPTICS: Partial<GlassOptics> = {
//     mapSize: 128,
//     frost: 6,
//     saturate: 1.2,
//     strength: 0.06,
//     depth: 0.5,
//     curvature: 0.35,
//     // A circle reads as glass mostly through its rim, so the meniscus is pushed
//     // slightly above the library default while the body dome stays gentle.
//     bend: 0.5,
//     bendWidth: 0.18,
//     dispersion: 0.3,
//     sheen: 0.35,
//     sheenWidth: 2,
//     glow: 0.12,
//     brightness: 0.06,
// };
export type TButtonPillGlassViewProps = {
    uid?: string;
    label?: string;
    iconName?: string;
    flagIcon?: string;
    iconPosition?: 'left' | 'right';
    size?: 'large' | 'medium' | 'small';
    fillContainer?: boolean;
    maxWidth?: string;
    margin?: TButtonOption['margin'];
    styleProps?: Pick<TCoreButtonStyleProps, 'width' | 'height'>;
    parentStyleNames?: string[];
    /** Theme style key for the button container. Defaults to `buttonPill`, same as the default render path. */
    styleName?: string;
    /** Used solely for theme resolution via useThemeHook inside CoreButtonView. Optional. */
    widget?: TCoreButtonViewProps['widget'];
    onClick?: MouseEventHandler<HTMLButtonElement>;
} & Pick<TWidgetCommonOption, 'isDisabled' | 'isHide'>;

/**
 * Liquid-glass render path of ButtonPillView, selected by `hierarchy === 'tertiaryGlass'`.
 *
 * The box (height, padding, radius, width clamping) is identical to the default path,
 * only split across two elements: the button carries the translucent on-dark tint, the
 * radius and the width clamp, while the lens — its single flex item — carries the height
 * and the padding and lays out the icon and the label. The padding has to live on the
 * lens for the refraction to reach the edges of the button rather than stopping at its
 * content box.
 *
 * The floating drop shadow is intentionally not supported here: the lens brings its
 * own rim light, and an outer shadow reads as a second, conflicting edge.
 */
export const ButtonPillGlassView = ({
    uid,
    label,
    iconName,
    flagIcon,
    iconPosition = 'left',
    size: buttonSize = 'large',
    fillContainer,
    maxWidth,
    margin,
    styleProps,
    parentStyleNames: parentStyleNamesProp,
    styleName = 'buttonPill',
    widget,
    isDisabled,
    isHide,
    onClick,
}: TButtonPillGlassViewProps) => {
    const paddingPostfix = getPaddingPostfix(!!(iconName || flagIcon), iconPosition, buttonSize);
    const padding = PADDING_MAP[paddingPostfix];
    const height = styleProps?.height ?? PILL_HEIGHT_MAP[buttonSize];

    const parentStyleNames = parentStyleNamesProp ?? [];
    const resolvedWidget: TCoreButtonViewProps['widget'] = widget ?? ({ uid: uid ?? '', data: { text: label }, options: {} } as TCoreButtonViewProps['widget']);

    return (
        <CoreButtonView
            uid={uid}
            widget={resolvedWidget}
            text={label}
            isDisabled={isDisabled}
            isHide={isHide}
            parentStyleNames={parentStyleNames}
            styleName={styleName}
            styleProps={{
                // A translucent tint keeps the backdrop visible for the lens to bend;
                // hover / press / disabled are colour swaps for the same reason — an
                // opacity change would start a new backdrop root and blank the lens.
                main: semanticColors[SemanticColor.FillTertiaryOnDark],
                onMain: semanticColors[SemanticColor.TextPrimaryOnDarkFill],
                fillContainer: fillContainer ?? false,
                maxWidth: getMaxWidth(maxWidth),
                ...styleProps,
                height,
                borderRadius: height / 2,
                fontStyle: PILL_FONT_STYLE_MAP[buttonSize],
                margin,
                cursorDisabled: 'default',
                hovered: {
                    main: semanticColors[SemanticColor.FillTertiaryOnDarkHovered],
                },
                pressed: {
                    main: semanticColors[SemanticColor.FillTertiaryOnDarkPressed],
                },
                disabled: {
                    main: semanticColors[SemanticColor.FillTertiaryOnDarkDisabled],
                    onMain: semanticColors[SemanticColor.TextPrimaryOnDarkFillDisabled],
                },
            }}
            onClick={onClick ?? (() => undefined)}
        >
            {/* Width is deliberately left out: the lens measures the box it ends up with, so
                the pill stays content-sized. `display: flex` comes through `style` because
                <Glass> pins `display: inline-block` inline, where a class cannot reach it. */}
            <Glass
                className={styles.lens}
                style={{
                    display: 'flex',
                    paddingTop: padding.top,
                    paddingRight: padding.right,
                    paddingBottom: padding.bottom,
                    paddingLeft: padding.left,
                }}
                height={height}
                radius={height / 2}
                optics={GLASS_OPTICS}
            >
                <ConditionalRender condition={!!(iconName || flagIcon)}>
                    <div className={clsx(styles.overlayed, iconPosition === 'right' && pillStyles.rightOrder)}>
                        <VisualView
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="buttonPillIcon"
                            iconVariant={'simple'}
                            size={buttonSize === 'small' ? 'xxSmall' : 'small'}
                            iconName={iconName}
                            flagIcon={flagIcon}
                            iconColor={
                                isDisabled ? semanticColors[SemanticColor.IconPrimaryOnDarkFillDisabled] : semanticColors[SemanticColor.IconPrimaryOnDarkFill]
                            }
                        />
                    </div>
                </ConditionalRender>
                <CoreTextView
                    text={label}
                    as="span"
                    className={styles.overlayed}
                    parentStyleNames={parentStyleNames}
                    styleName={'buttonPillText'}
                    isDisabled={isDisabled}
                    styleProps={{
                        main: semanticColors[SemanticColor.TextPrimaryOnDarkFill],
                        disabled: {
                            main: semanticColors[SemanticColor.TextPrimaryOnDarkFillDisabled],
                        },
                    }}
                />
            </Glass>
        </CoreButtonView>
    );
};
